import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  adminFilterDateInputClasses,
  adminFormTextareaClasses,
  withoutEscalatedAuditLogEntries,
  getAdminNoteAddedByDisplay,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
  ADMIN_TOOLBAR_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import useClickOutside from '../../../Hooks/useClickOutside';

const SEVERITY_BADGE_CLASSES = {
  'Level 1 — Low': 'bg-freshgreen text-white',
  'Level 2 — Medium': 'bg-warmyellow text-white',
  'Level 3 — High': 'bg-alertred text-white',
  'Level 4 — Critical': 'bg-alertred text-white font-bold',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format datetime for Admin Notes display: "14 Jan 2026 · 10:42 AM"
const formatNoteDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} · ${timePart}`;
};

const AutoFlagDetail = () => {
  const { flagId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const itemFromState = location.state?.item;

  const [activeTab, setActiveTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  // Audit log filters state
  const [auditFilters, setAuditFilters] = useState({
    dateRange: 'All',
    actor: 'All',
    module: 'All',
    action: 'All',
    outcome: 'All'
  });
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [actorOpen, setActorOpen] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  
  const dateRangeRef = useRef(null);
  const actorRef = useRef(null);
  const moduleRef = useRef(null);
  const actionRef = useRef(null);
  const outcomeRef = useRef(null);
  
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));
  useClickOutside(actorRef, () => setActorOpen(false));
  useClickOutside(moduleRef, () => setModuleOpen(false));
  useClickOutside(actionRef, () => setActionOpen(false));
  useClickOutside(outcomeRef, () => setOutcomeOpen(false));

  // Admin Notes state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({
    noteText: ''
  });

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 350));
      
      // Use data from state if available, otherwise generate
      const flag = itemFromState || {
        id: `SF#${flagId}`,
        flagId: `SF#${flagId}`,
        ruleId: 'RL-001',
        ruleName: 'Document Expiry Check',
        ruleCategory: 'Compliance — Documents',
        severity: 'Level 2 — Medium',
        status: 'New',
        contentType: 'Document & Licence',
        entityId: 'DOC#0001',
        triggerDataSummary: 'Expiry Date: 12 Aug 2024',
        linkedCaseId: null,
        triggeredOn: new Date().toISOString(),
      };

      const now = new Date();
      const triggeredOnDate = new Date(now);
      triggeredOnDate.setDate(triggeredOnDate.getDate() - 2);

      setDetail({
        ...flag,
        triggerCount: 3,
        falsePositiveRate: '0%',
        enabled: true,
        lastUpdated: new Date().toISOString(),
        triggerData: {
          expiryDate: '12 Aug 2024',
          licenceType: 'Electrical',
          ruleCondition: 'expiry_date < today',
          triggeredFrom: 'Verification Module',
          capturedAt: triggeredOnDate.toISOString(),
          capturedField1: 'LicenceExpiry = 12 Aug 2024',
          capturedField2: 'LicenceType = Electrical',
          repeatedPatternCount: 1,
          metadata: 'Document verification scan',
        },
        ruleDefinition: {
          ruleId: flag.ruleId,
          ruleName: flag.ruleName,
          category: flag.ruleCategory || 'Compliance — Documents',
          severity: flag.severity,
          triggerCondition: 'expiry_date < today',
          requiredFields: ['LicenceExpiry', 'LicenceType'],
          evaluationFrequency: 'Daily',
          autoFlagEnabled: true,
          allowedOverrides: 'Senior Moderator, Compliance Team',
          lastUpdated: new Date().toISOString(),
          ownedBy: 'Compliance Team',
        },
        linkedCase: flag.linkedCaseId ? {
          caseId: flag.linkedCaseId,
          caseStatus: 'In Review',
          severity: flag.severity,
          assignedAdmin: 'John Smith',
          linkedOn: triggeredOnDate.toISOString(),
        } : null,
        auditLogs: [
          {
            timestamp: formatDateTime(triggeredOnDate.toISOString()),
            performedBy: 'System',
            module: 'Rules Engine',
            event: 'Flag created',
            details: `Auto-flag ${flag.flagId} created by rule ${flag.ruleId}`,
            outcome: 'Success',
            ref: { id: flag.flagId, type: 'flag' }
          },
          {
            timestamp: formatDateTime(new Date(triggeredOnDate.getTime() + 3600000).toISOString()),
            performedBy: 'Admin: R.B.',
            module: 'Moderation',
            event: 'Status changed',
            details: `Flag status changed from New to Assigned`,
            outcome: 'Success',
            ref: { id: flag.flagId, type: 'flag' }
          },
          {
            timestamp: formatDateTime(new Date(triggeredOnDate.getTime() + 7200000).toISOString()),
            performedBy: 'Admin: R.B.',
            module: 'Moderation',
            event: 'Linked to case',
            details: `Flag linked to moderation case ${flag.linkedCaseId || 'MC#001'}`,
            outcome: 'Success',
            ref: { id: flag.linkedCaseId || 'MC#001', type: 'case' }
          },
        ],
        notes: [
          {
            id: 'note-1',
            timestamp: formatDateTime(new Date(triggeredOnDate.getTime() + 10800000).toISOString()),
            author: 'Rachel Brown',
            adminName: 'Moderator — Rachel Brown',
            email: 'admin@tradesposter.com',
            category: 'Compliance',
            content: 'Flag verified. Document expiry detected. Business has been notified to update licence.',
            outcome: 'Resolved',
            linkedRef: { id: flag.flagId, type: 'flag' }
          },
        ],
      });
      
      setLoading(false);
    };

    loadDetail();
  }, [flagId, itemFromState]);

  const renderOverview = () => {
    if (!detail) return null;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Flag ID</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.flagId}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Rule ID</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.ruleId}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Rule Name</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.ruleName}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Rule Category</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.ruleDefinition?.category || '—'}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Severity Level</AdminDetailFieldLabel>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${SEVERITY_BADGE_CLASSES[detail.severity] || 'bg-mediumgray text-white'}`}>
              {detail.severity}
            </span>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Flag Status</AdminDetailFieldLabel>
            <AdminStatusBadge status={detail.status} label={detail.status} />
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Content Type</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.contentType}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Entity ID</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.entityId}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Trigger Count (Last 30 days)</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.triggerCount || '—'}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">False Positive Rate</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.falsePositiveRate || '—'}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Enabled</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.enabled ? 'Yes' : 'No'}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Triggered On</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{formatDateTime(detail.triggeredOn)}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Last Updated</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{formatDateTime(detail.lastUpdated)}</div>
          </div>
          {detail.linkedCaseId && (
            <div className="space-y-1">
              <AdminDetailFieldLabel className="mb-1">Linked Case ID</AdminDetailFieldLabel>
              <button
                onClick={() => navigate(`/admin/moderation/queue/${detail.linkedCaseId.replace('MC#', '')}`)}
                className="text-primaryblue hover:text-offblack text-sm font-medium"
              >
                {detail.linkedCaseId}
              </button>
            </div>
          )}
        </div>
    );
  };

  const renderTriggerData = () => {
    if (!detail || !detail.triggerData) return null;

    return (
      <div className="space-y-4">
          <AdminPanelTitle className="text-offblack">Trigger Data</AdminPanelTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(detail.triggerData).map(([key, value]) => (
              <div key={key} className="space-y-1">
                <AdminDetailFieldLabel className="mb-1">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </AdminDetailFieldLabel>
                <div className="text-sm text-offblack font-medium">{value || '—'}</div>
              </div>
            ))}
          </div>
      </div>
    );
  };

  const renderRuleDefinition = () => {
    if (!detail || !detail.ruleDefinition) return null;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {Object.entries(detail.ruleDefinition).map(([key, value]) => (
            <div key={key} className="space-y-1">
              <AdminDetailFieldLabel className="mb-1">
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </AdminDetailFieldLabel>
              <div className="text-sm text-offblack font-medium">{value || '—'}</div>
            </div>
          ))}
      </div>
    );
  };

  // Filter change handler
  const handleFilterChange = (filterType, value) => {
    setAuditFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Generate CSV content
  const generateCSV = (logs, filters) => {
    const headers = ['Timestamp (AEST)', 'Performed By', 'Module', 'Event', 'Details', 'Outcome', 'Ref'];
    const rows = logs.map(log => [
      log.timestamp,
      log.performedBy || '—',
      log.module || '—',
      log.event || '—',
      log.details || '—',
      log.outcome || '—',
      log.ref?.id || '—'
    ]);
    
    const csvRows = [headers, ...rows];
    return csvRows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  };

  // Admin Notes handlers
  const openNotesModal = () => {
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setNoteForm({ noteText: '' });
  };

  const handleNoteSave = () => {
    if (!noteForm.noteText || noteForm.noteText.trim() === '') {
      alert('Note is required');
      return;
    }

    const now = new Date();
    const timestamp = formatDateTime(now.toISOString());
    
    // Add new note
    // In real app, get admin name and team from auth context
    const newNote = {
      id: `note-${Date.now()}`,
      timestamp: timestamp,
      createdAt: now.toISOString(),
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      content: noteForm.noteText.trim(),
      noteContent: noteForm.noteText.trim()
    };
    
    setDetail(prev => ({
      ...prev,
      notes: [newNote, ...(prev.notes || [])]
    }));
    
    closeNotesModal();
  };

  const getRefLink = (ref) => {
    if (!ref || !ref.id) return null;
    switch (ref.type) {
      case 'flag':
        return `/admin/moderation/violations/auto-flag/${ref.id.replace('SF#', '')}`;
      case 'case':
        return `/admin/moderation/queue/${ref.id.replace('MC#', '')}`;
      default:
        return null;
    }
  };

  const getRefDisplay = (ref) => {
    if (!ref || !ref.id) return '—';
    return ref.id;
  };

  // Filter logs based on current filters
  const filteredAuditLogs = useMemo(() => {
    if (!detail || !detail.auditLogs) return [];
    
    let logs = [...detail.auditLogs];
    
    if (auditFilters.actor !== 'All') {
      logs = logs.filter(log => log.performedBy === auditFilters.actor);
    }
    
    if (auditFilters.module !== 'All') {
      logs = logs.filter(log => log.module === auditFilters.module);
    }
    
    if (auditFilters.action !== 'All') {
      logs = logs.filter(log => log.event === auditFilters.action);
    }
    
    if (auditFilters.outcome !== 'All') {
      logs = logs.filter(log => log.outcome === auditFilters.outcome);
    }
    
    return withoutEscalatedAuditLogEntries(logs);
  }, [detail?.auditLogs, auditFilters]);

  const renderLinkedCase = () => {
    if (!detail || !detail.linkedCase) {
      return (
        <AdminSupportingText className="text-mediumgray">
          No linked moderation case.
        </AdminSupportingText>
      );
    }

    const caseData = detail.linkedCase;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Moderation Case ID</AdminDetailFieldLabel>
            <button
              onClick={() => navigate(`/admin/moderation/queue/${caseData.caseId.replace('MC#', '')}`)}
              className="text-primaryblue hover:text-offblack text-sm font-medium"
            >
              {caseData.caseId}
            </button>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Case Status</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{caseData.caseStatus}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Severity</AdminDetailFieldLabel>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${SEVERITY_BADGE_CLASSES[caseData.severity] || 'bg-mediumgray text-white'}`}>
              {caseData.severity}
            </span>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Assigned Admin</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{caseData.assignedAdmin}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Linked On</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{formatDateTime(caseData.linkedOn)}</div>
          </div>
        </div>
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-wrap gap-3">
              <AdminHeaderAction
              onClick={() => navigate(`/admin/moderation/queue/${caseData.caseId.replace('MC#', '')}`)}
            >
              View Case
            </AdminHeaderAction>
          </div>
        </div>
      </div>
    );
  };

  const renderAuditLog = () => {
    if (!detail || !detail.auditLogs) return null;
    
    return (
      <div className="space-y-4">
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <div className="flex gap-3 flex-wrap">
              {/* Date Range Filter */}
              <div className="relative" ref={dateRangeRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setDateRangeOpen(!dateRangeOpen);
                    setActorOpen(false);
                    setModuleOpen(false);
                    setActionOpen(false);
                    setOutcomeOpen(false);
                  }}
                >
                  <span>Date range: {auditFilters.dateRange}</span>
                  <i className={`fi ${dateRangeOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {dateRangeOpen && (
                  <div className="flex flex-col rounded w-36 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'Last 7 days', 'Last 30 days', 'Custom'].map((option) => (
                      <AdminFilterMenuItem
                        key={option}
                        active={auditFilters.dateRange === option}
                        onClick={() => {
                          handleFilterChange('dateRange', option);
                          setDateRangeOpen(false);
                        }}
                      >
                        {option}
                      </AdminFilterMenuItem>
                    ))}
                  </div>
                )}
              </div>

              {/* Performed By Filter */}
              <div className="relative" ref={actorRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setActorOpen(!actorOpen);
                    setDateRangeOpen(false);
                    setModuleOpen(false);
                    setActionOpen(false);
                    setOutcomeOpen(false);
                  }}
                >
                  <span>Performed By: {auditFilters.actor}</span>
                  <i className={`fi ${actorOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {actorOpen && (
                  <div className="flex flex-col rounded w-40 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'System', 'Admin: R.B.', 'Admin: S.J.', 'Moderator: J.S.'].map((option) => (
                      <AdminFilterMenuItem
                        key={option}
                        active={auditFilters.actor === option}
                        onClick={() => {
                          handleFilterChange('actor', option);
                          setActorOpen(false);
                        }}
                      >
                        {option}
                      </AdminFilterMenuItem>
                    ))}
                  </div>
                )}
              </div>

              {/* Module Filter */}
              <div className="relative" ref={moduleRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setModuleOpen(!moduleOpen);
                    setDateRangeOpen(false);
                    setActorOpen(false);
                    setActionOpen(false);
                    setOutcomeOpen(false);
                  }}
                >
                  <span>Module: {auditFilters.module}</span>
                  <i className={`fi ${moduleOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {moduleOpen && (
                  <div className="flex flex-col rounded w-40 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'Rules Engine', 'Moderation', 'Compliance', 'Support'].map((option) => (
                      <AdminFilterMenuItem
                        key={option}
                        active={auditFilters.module === option}
                        onClick={() => {
                          handleFilterChange('module', option);
                          setModuleOpen(false);
                        }}
                      >
                        {option}
                      </AdminFilterMenuItem>
                    ))}
                  </div>
                )}
              </div>

              {/* Event Filter */}
              <div className="relative" ref={actionRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setActionOpen(!actionOpen);
                    setDateRangeOpen(false);
                    setActorOpen(false);
                    setModuleOpen(false);
                    setOutcomeOpen(false);
                  }}
                >
                  <span>Event: {auditFilters.action}</span>
                  <i className={`fi ${actionOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {actionOpen && (
                  <div className="flex flex-col rounded w-48 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'Flag created', 'Status changed', 'Linked to case', 'Marked FP', 'Verified'].map((option) => (
                      <AdminFilterMenuItem
                        key={option}
                        active={auditFilters.action === option}
                        onClick={() => {
                          handleFilterChange('action', option);
                          setActionOpen(false);
                        }}
                      >
                        {option}
                      </AdminFilterMenuItem>
                    ))}
                  </div>
                )}
              </div>

              {/* Outcome Filter */}
              <div className="relative" ref={outcomeRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setOutcomeOpen(!outcomeOpen);
                    setDateRangeOpen(false);
                    setActorOpen(false);
                    setModuleOpen(false);
                    setActionOpen(false);
                  }}
                >
                  <span>Outcome: {auditFilters.outcome}</span>
                  <i className={`fi ${outcomeOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {outcomeOpen && (
                  <div className="flex flex-col rounded w-36 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'Success', 'Failed', 'Blocked', 'Reverted'].map((option) => (
                      <AdminFilterMenuItem
                        key={option}
                        active={auditFilters.outcome === option}
                        onClick={() => {
                          handleFilterChange('outcome', option);
                          setOutcomeOpen(false);
                        }}
                      >
                        {option}
                      </AdminFilterMenuItem>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Export CSV Button */}
              <AdminHeaderAction
              onClick={() => {
                const csvContent = generateCSV(filteredAuditLogs, auditFilters);
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `flag-audit-logs-${detail.flagId}-${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Export CSV
            </AdminHeaderAction>
            </div>
          </div>
        
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="overflow-hidden">
            {/* Header */}
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(140px, 1.2fr) minmax(120px, 1fr) minmax(100px, 0.9fr) minmax(180px, 1.8fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr)' }}>
              <div className="flex items-center">
                <AdminTableHeader>Timestamp (AEST)</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Performed By</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Module</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Event</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Details</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Outcome</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Ref</AdminTableHeader></div>
            </div>
            
            {/* Rows */}
            {filteredAuditLogs.length === 0 ? (
              <div className="py-10 text-center">
                <AdminSupportingText>
                  No audit logs found matching the filters.
                </AdminSupportingText>
              </div>
            ) : (
              filteredAuditLogs.map((log, idx) => {
                const refLink = getRefLink(log.ref);
                const outcomeColor = log.outcome === 'Success' 
                  ? 'bg-freshgreen' 
                  : log.outcome === 'Failed'
                  ? 'bg-alertred'
                  : log.outcome === 'Blocked'
                  ? 'bg-orange-500'
                  : log.outcome === 'Reverted'
                  ? 'bg-warmyellow'
                  : 'bg-mediumgray';

                return (
                  <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(140px, 1.2fr) minmax(120px, 1fr) minmax(100px, 0.9fr) minmax(180px, 1.8fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr)' }}>
                    <div className="flex items-center">
                      <span className="">{log.timestamp}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="">{log.performedBy || '—'}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="">{log.module || '—'}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="">{log.event || '—'}</span>
                    </div>
                    <div className="flex items-center min-w-0">
                      <span className=" text-offblack truncate" title={log.details}>
                        {log.details || '—'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className={`px-2 py-1 rounded-full text-xs text-white ${outcomeColor}`}>
                        {log.outcome || '—'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      {refLink && log.ref?.id ? (
                        <Link
                          to={refLink}
                          className="inline-flex px-2 py-1 rounded bg-lightgray text-offblack text-xs font-medium hover:bg-primaryblue hover:text-white transition-colors"
                        >
                          {log.ref.id}
                        </Link>
                      ) : (
                        <span className=" text-mediumgray">—</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DashboardMainContainer>
      </div>
    );
  };

  const renderAdminNotes = () => {
    if (!detail) return null;
    
    return (
      <div className="space-y-4">
          <div className={ADMIN_TOOLBAR_CLASS}>
            <div className="flex justify-end items-center w-full">
              <AdminHeaderAction
              onClick={() => openNotesModal()}
              >
                Add Note
              </AdminHeaderAction>
            </div>
          </div>
          
          {/* Notes Table */}
          <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
            {/* Header */}
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
              <div className="flex items-center">
                <span className="font-semibold">Created At</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Added By</span>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Note</AdminTableHeader></div>
            </div>
            
            {/* Rows - Sort by date (newest first) */}
            {(() => {
              const sortedNotes = [...(detail.notes || [])].sort((a, b) => {
                const aDate = new Date(a.createdAt || a.timestamp || 0);
                const bDate = new Date(b.createdAt || b.timestamp || 0);
                return bDate - aDate;
              });

              return sortedNotes.length > 0 ? sortedNotes.map((note) => {
                const noteContent = note.noteContent || note.content || '';
                const createdAt = note.createdAt ? formatNoteDateTime(note.createdAt) : 
                                 (note.timestamp ? formatNoteDateTime(new Date(note.timestamp)) : '—');
                
                const addedByDisplay = getAdminNoteAddedByDisplay(note);

                return (
                  <div key={note.id} className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
                    <div className="flex items-start">
                      <span className="">{createdAt}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="font-medium">{addedByDisplay}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="whitespace-pre-wrap">{noteContent}</span>
                    </div>
                  </div>
                );
              }) : (
                <div className="py-8 text-center">
                  <span className="text-mediumgray">No notes yet</span>
                </div>
              );
            })()}
          </DashboardMainContainer>

          {/* Add Note Modal */}
          {notesModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeNotesModal}>
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <AdminPanelTitle className="text-offblack">
                    Add Note
                  </AdminPanelTitle>
                  <button
                    onClick={closeNotesModal}
                    className="text-offblack hover:text-primaryblue"
                  >
                    <i className="fi fi-rr-cross dashboard-icon"></i>
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Note - Required */}
                  <div>
                    <AdminDetailFieldLabel className="mb-1.5">
                      Note <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <textarea
                      value={noteForm.noteText}
                      onChange={(e) => setNoteForm(prev => ({ ...prev, noteText: e.target.value }))}
                      className={adminFormTextareaClasses()}
                      placeholder="Enter note..."
                      required
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4">
                    <AdminFormSecondaryAction type="button" onClick={closeNotesModal}
                      >Cancel</AdminFormSecondaryAction>
              <AdminHeaderAction
              onClick={handleNoteSave}
                    >
                      Save Note
                    </AdminHeaderAction>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    );
  };

  const tabs = useMemo(() => [
    { id: 'overview', label: 'Overview' },
    { id: 'trigger-data', label: 'Trigger Data' },
    { id: 'rule-definition', label: 'Rule Definition' },
    { id: 'linked-case', label: 'Linked Moderation Case' },
    { id: 'audit-log', label: 'Flag Audit Log' },
    { id: 'notes', label: 'Admin Notes' },
  ], []);

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'trigger-data':
        return renderTriggerData();
      case 'rule-definition':
        return renderRuleDefinition();
      case 'linked-case':
        return renderLinkedCase();
      case 'audit-log':
        return renderAuditLog();
      case 'notes':
        return renderAdminNotes();
      default:
        return renderOverview();
    }
  };

  if (loading || !detail) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading auto-flag details...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="space-y-2">
                <AdminPanelTitle className="text-offblack">
                  {detail.flagId} — {detail.ruleName}
                </AdminPanelTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${SEVERITY_BADGE_CLASSES[detail.severity] || 'bg-mediumgray text-white'}`}>
                    {detail.severity}
                  </span>
                  <AdminStatusBadge status={detail.status} label={detail.status} />
                </div>
              </div>
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          <div className={adminDetailTabContentClassName(activeTab)}>
            {renderActiveTab()}
          </div>
        </DashboardMainContainer>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AutoFlagDetail;

